/**
 * Color Switcher Component
 * Handles color variant switching without page reload
 */
import {
  applyVariantPriceToDom,
  findMatchingVariantFromSelection,
  fetchProductSectionDocument
} from "./variant-picker.js";

/** Radio `name` is "Size" in the DOM; lowercasing it makes querySelector miss. */
function radiosByOptionName(root, optionName) {
  const lower = String(optionName || "").toLowerCase();
  return [...root.querySelectorAll('input[type="radio"]')].filter((radio) => radio.name.toLowerCase() === lower);
}

function colorSwitcher() {
  return {
    showTooltip: false,
    _prefetchTimer: null,

    /**
     * Warm the section cache on hover/touch intent so the actual click is a cache HIT
     * (instant swap) instead of waiting on the network. Debounced so quickly sweeping
     * the mouse across swatches doesn't fire a fetch per swatch, and cancelled on leave.
     * @param {string} url - The target colour's URL (query is stripped).
     * @param {string} [heroImageUrl] - The target colour's hero image, warmed in parallel.
     */
    prefetchColor(url, heroImageUrl) {
      clearTimeout(this._prefetchTimer);
      this._prefetchTimer = setTimeout(() => this.warmColorSection(url, heroImageUrl), 120);
    },

    cancelPrefetch() {
      clearTimeout(this._prefetchTimer);
    },

    warmColorSection(url, heroImageUrl) {
      try {
        // Respect Save-Data and slow connections — don't spend the user's data on a guess.
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (connection?.saveData) return;
        if (["slow-2g", "2g"].includes(connection?.effectiveType)) return;

        // Warm the hero image into the browser cache so the click can paint it instantly.
        if (heroImageUrl) {
          const img = new Image();
          img.src = heroImageUrl;
        }

        const sectionId = this.$el.closest("[data-section]")?.getAttribute("data-section");
        if (!sectionId) return;

        // Same key handleColorChange uses, so the click resolves from cache (or coalesces
        // onto this in-flight request). Fire-and-forget; errors are non-fatal.
        fetchProductSectionDocument(url.split("?")[0], sectionId);
      } catch (error) {
        // Prefetch is best-effort; never surface errors to the user.
      }
    },

    /**
     * Paint the new colour's hero image immediately from the URL we already know (rendered
     * by the swatch), before the section fetch resolves. The full gallery — thumbnails,
     * zoom, extra angles — is reconciled by updateMediaGallery when the fetch returns; this
     * just removes the perceived wait for the main image. No-op if the URL/element is absent.
     * @param {string} imageUrl
     */
    setHeroImageOptimistically(imageUrl) {
      if (!imageUrl) return;

      const heroImg = document.querySelector("#variant-image ul li:first-child img");
      if (!heroImg) return;

      // Clear srcset/sizes so the explicit src wins (otherwise the old srcset keeps showing).
      heroImg.srcset = "";
      heroImg.sizes = "";
      heroImg.src = imageUrl;
    },

    /**
     * Main handler for color switching
     * @param {string} url - The URL of the new color variant
     * @param {Event} event - The click event
     * @param {string} [heroImageUrl] - The new colour's hero image, painted immediately.
     */
    async handleColorChange(url, event, heroImageUrl) {
      try {
        // Paint the new hero image right away (parallel to the section fetch below).
        this.setHeroImageOptimistically(heroImageUrl);

        // Get current size value.
        const options = [...document.querySelectorAll(".variant-option")];
        let currentSize = null;

        options.forEach((option) => {
          const input = option.querySelector("input");

          if (!input || input.name.toLowerCase() !== "size") {
            return;
          }

          if (input.checked) {
            currentSize = input.value;
          }
        });

        // Set global flags to coordinate with variant-picker
        window._colorSwitchInProgress = true;
        window._renderProductInfoCalled = false;
        // Stash size before the picker swap so Alpine init (and master-child fetch)
        // can restore it even when the new colour's first available size differs.
        window._pendingColorSwitchSize = currentSize;

        // Update selected state visually for immediate feedback
        const clickedElement = event?.target?.closest("li") || this.$el;
        const colorList = clickedElement.parentElement;

        this.updateSelectedState(clickedElement, colorList);

        // Resolve the current section id up-front so we can request just that section
        // for the new colour instead of downloading the entire PDP document.
        const sectionElement = this.$el.closest("[data-section]");
        const sectionId = sectionElement?.getAttribute("data-section");

        // Fetch only the product's `main` section (Shopify Section Rendering API).
        // The swatch link arrives with ?section_id=…&size=… appended, which we don't use
        // here (current size is read from the DOM above, and pushState strips the query).
        // Strip it so this is a clean ?sections=<id> request instead of a redundant
        // section_id=…&size=…&sections=… combo.
        const productBaseUrl = url.split("?")[0];
        const doc = await fetchProductSectionDocument(productBaseUrl, sectionId);

        // Get the PDP hero section
        const currentPdpSection = document.querySelector("#pdp-hero-section");

        if (currentPdpSection && sectionId) {
          // Add subtle fade effect
          currentPdpSection.style.transition = "opacity 0.15s ease";
          currentPdpSection.style.opacity = "0.85";

          // Let the fade actually paint before the (synchronous) DOM swap — wait one
          // rendered frame instead of guessing with a timer.
          await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

          // Get current selected options and apply them to the new document
          // Check for both regular variant-selects and bundle-selects (for bundle products)
          const currentVariantSelects =
            document.querySelector(`#variant-selects-${sectionId}`) ||
            document.querySelector(`#bundle-selects-${sectionId}`);
          const docVariantSelects =
            doc.querySelector(`#variant-selects-${sectionId}`) || doc.querySelector(`#bundle-selects-${sectionId}`);

          if (currentVariantSelects && docVariantSelects) {
            // Find ALL checked radios directly (works for all product types)
            const currentCheckedRadios = currentVariantSelects.querySelectorAll('input[type="radio"]:checked');

            // Store selected values to check availability later
            const selectedValues = {};
            currentCheckedRadios.forEach((radio) => {
              selectedValues[radio.name] = radio.value;
            });

            currentCheckedRadios.forEach((checkedRadio) => {
              const selectedValue = checkedRadio.value;
              const optionName = checkedRadio.name;

              // Get all radios with this name in the new doc (name case differs: Size vs size)
              const docRadiosWithSameName = radiosByOptionName(docVariantSelects, optionName);

              // Clear all selections first
              docRadiosWithSameName.forEach((radio) => {
                radio.removeAttribute("checked");
                radio.checked = false;
              });

              let matchingRadio = docRadiosWithSameName.find((radio) => radio.value === selectedValue) || null;

              // Special case: If Supplier or Fabric not found, select the first available option
              const optionNameLower = optionName.toLowerCase();
              if (
                !matchingRadio &&
                (optionNameLower === "supplier" || optionNameLower === "fabric") &&
                docRadiosWithSameName.length > 0
              ) {
                matchingRadio = docRadiosWithSameName[0];
              }

              if (matchingRadio) {
                matchingRadio.setAttribute("checked", "checked");
                matchingRadio.checked = true;

                // Also set visual classes on the label to prevent flicker
                const listItem = matchingRadio.closest("li");
                const label = listItem?.querySelector("label");
                if (label) {
                  label.classList.add("!bg-black", "!text-white", "!outline-black");
                  label.style.zIndex = "2";
                }
              }
            });

            // Note: We check supplier availability AFTER updateVariantPicker to ensure
            // we're working with the final DOM state
          }

          // 1. Update media gallery only (left side)
          this.updateMediaGallery(currentPdpSection, doc);

          // 2. Update variant picker (pass current size so Alpine init does not fall back
          // to the new colour's first available — master colours often differ here)
          this.updateVariantPicker(sectionElement, doc, currentSize);

          // 2.1. Keep the current size on the new colour whenever that size exists
          this.selectSize(currentSize, sectionId);

          // 2.2. CRITICAL: Check if the selected combination is available in new color
          // Must run AFTER updateVariantPicker because that replaces the DOM
          const updatedVariantSelects =
            document.querySelector(`#variant-selects-${sectionId}`) ||
            document.querySelector(`#bundle-selects-${sectionId}`);
          if (updatedVariantSelects) {
            this.checkAndSwitchSupplierIfNeeded(doc, updatedVariantSelects);
          }

          // Instant price from variantData (before Alpine init / renderProductInfo fetch)
          this.syncPriceFromSelection(sectionElement, sectionId);

          // 3. Update title only; renderProductInfo handles price and buttons for the selected variant
          this.updateProductTitleOnly(sectionElement, doc, sectionId);

          // 4. Update comparison/pricing tables (desktop + mobile) and bedding drawer content
          this.updatePricingTables(sectionElement, doc);

          // Reset opacity
          currentPdpSection.style.opacity = "1";

          // Wait for Alpine to flush DOM updates from the picker swap (deterministic).
          await new Promise((resolve) => this.$nextTick(resolve));

          // Alpine init can overwrite radios with first-available after selectSize;
          // re-apply once the picker is live so master colours keep the same size.
          this.selectSize(currentSize, sectionId);

          // Reset thumbnail scroll
          this.resetThumbnailScroll(currentPdpSection);
        }

        // Update the URL
        const baseUrl = url.split("?")[0];
        window.history.pushState({}, "", baseUrl);

        // Clear the coordination flags after Alpine has flushed the picker re-init,
        // instead of guessing with a 200ms timer.
        await new Promise((resolve) => this.$nextTick(resolve));
        window._colorSwitchInProgress = false;
        window._renderProductInfoCalled = false;
        window._pendingColorSwitchSize = null;
      } catch (error) {
        window._colorSwitchInProgress = false;
        window._renderProductInfoCalled = false;
        window._pendingColorSwitchSize = null;
        console.error("Error updating color:", error);
        // Fallback to page reload if async fails
        window.location.href = url.split("?")[0];
      }
    },

    /**
     * Check if the selected variant is available, and if not, try switching supplier or fabric
     */
    checkAndSwitchSupplierIfNeeded(doc, docVariantSelects) {
      // Get all checked radios to build current selection
      const checkedRadios = docVariantSelects.querySelectorAll('input[type="radio"]:checked');
      const selectedOptions = {};
      checkedRadios.forEach((radio) => {
        selectedOptions[radio.name] = radio.value;
      });

      // Check if the NEW product has a Supplier option (even if not selected yet)
      const supplierRadios = docVariantSelects.querySelectorAll('input[type="radio"][name="Supplier"]');
      if (supplierRadios.length === 0) {
        return;
      }

      // Extract variant data from the document
      // The script is a sibling of variant-selects, so look in the parent (variant picker wrapper)
      const variantPickerWrapper = docVariantSelects.closest('[x-data*="variantPicker"]');
      const variantDataScript = variantPickerWrapper?.querySelector('script[x-ref="variantData"]');
      if (!variantDataScript) {
        return;
      }

      let variantData;
      try {
        variantData = JSON.parse(variantDataScript.textContent);
      } catch (e) {
        console.error("Failed to parse variant data:", e);
        return;
      }

      // Auto-fill Supplier from variant data if not already selected
      // Use first AVAILABLE variant to avoid selecting out-of-stock suppliers
      if (!selectedOptions.Supplier && variantData.length > 0) {
        const firstAvailableVariant = variantData.find((v) => v.available) || variantData[0];
        const variantOptions = [
          firstAvailableVariant.option1,
          firstAvailableVariant.option2,
          firstAvailableVariant.option3
        ];

        // Find which option is the Supplier by checking if it matches a supplier radio value
        supplierRadios.forEach((radio) => {
          if (variantOptions.includes(radio.value)) {
            selectedOptions.Supplier = radio.value;

            // Also set it as checked in the DOM (but don't add visual classes yet)
            // We'll only add visual classes if we keep this supplier
            radio.setAttribute("checked", "checked");
            radio.checked = true;
          }
        });
      }

      // Auto-fill Fabric from variant data if not already selected
      // Use first AVAILABLE variant to avoid selecting out-of-stock fabrics
      const fabricRadios = docVariantSelects.querySelectorAll('input[type="radio"][name="Fabric"]');
      if (fabricRadios.length > 0 && !selectedOptions.Fabric && variantData.length > 0) {
        const firstAvailableVariant = variantData.find((v) => v.available) || variantData[0];
        const variantOptions = [
          firstAvailableVariant.option1,
          firstAvailableVariant.option2,
          firstAvailableVariant.option3
        ];

        // Find which option is the Fabric by checking if it matches a fabric radio value
        fabricRadios.forEach((radio) => {
          if (variantOptions.includes(radio.value)) {
            selectedOptions.Fabric = radio.value;

            // Also set it as checked in the DOM (but don't add visual classes yet)
            radio.setAttribute("checked", "checked");
            radio.checked = true;
          }
        });
      }

      // Find the matching variant based on selected options
      // Note: The color is already part of the variant (not a selectable option)
      // so we only match the selectable options (Size, Supplier, Fabric)
      const matchingVariant = variantData.find((variant) => {
        const variantOptions = [variant.option1, variant.option2, variant.option3];

        // Check if ALL selected option values are present in this variant's options
        return Object.values(selectedOptions).every((selectedValue) => {
          return variantOptions.includes(selectedValue);
        });
      });

      // If the variant IS available, add visual classes to the selected supplier
      if (matchingVariant && matchingVariant.available && selectedOptions.Supplier) {
        const selectedSupplierRadio = docVariantSelects.querySelector(
          `input[type='radio'][name="Supplier"][value="${selectedOptions.Supplier}"]`
        );
        if (selectedSupplierRadio) {
          const listItem = selectedSupplierRadio.closest("li");
          const label = listItem?.querySelector("label");
          if (label) {
            label.classList.add("!bg-black", "!text-white", "!outline-black");
            label.style.zIndex = "2";
          }
        }
        return;
      }

      // If variant is NOT available, try switching Supplier or Fabric
      if (matchingVariant && !matchingVariant.available) {
        // Try switching Supplier first (if exists)
        if (selectedOptions.Supplier) {
          // Find the OTHER supplier (not currently selected)
          let alternativeSupplier = null;
          supplierRadios.forEach((radio) => {
            if (radio.value !== selectedOptions.Supplier) {
              alternativeSupplier = radio;
            }
          });

          if (alternativeSupplier) {
            // Check if the alternative supplier has this size in stock
            const alternativeVariant = variantData.find((variant) => {
              const variantOptions = [variant.option1, variant.option2, variant.option3];
              return (
                variantOptions.includes(selectedOptions.Size) && variantOptions.includes(alternativeSupplier.value)
              );
            });

            if (alternativeVariant && alternativeVariant.available) {
              // Clear all supplier selections
              supplierRadios.forEach((radio) => {
                radio.removeAttribute("checked");
                radio.checked = false;
                // Remove visual classes
                const listItem = radio.closest("li");
                const label = listItem?.querySelector("label");
                if (label) {
                  label.classList.remove("!bg-black", "!text-white", "!outline-black");
                  label.style.zIndex = "";
                }
              });

              // Select the alternative supplier
              alternativeSupplier.setAttribute("checked", "checked");
              alternativeSupplier.checked = true;

              // Add visual classes
              const listItem = alternativeSupplier.closest("li");
              const label = listItem?.querySelector("label");
              if (label) {
                label.classList.add("!bg-black", "!text-white", "!outline-black");
                label.style.zIndex = "2";
              }
              return; // Exit early if supplier switch worked
            }
          }
        }

        // Try switching Fabric (if exists and supplier switch didn't work)
        if (selectedOptions.Fabric) {
          const fabricRadios = docVariantSelects.querySelectorAll('input[type="radio"][name="Fabric"]');
          if (fabricRadios.length > 0) {
            // Try each Fabric value until we find an available variant
            for (const fabricRadio of fabricRadios) {
              if (fabricRadio.value === selectedOptions.Fabric) continue; // Skip current

              // Check if this fabric has this size in stock
              const alternativeVariant = variantData.find((variant) => {
                const variantOptions = [variant.option1, variant.option2, variant.option3];
                return variantOptions.includes(selectedOptions.Size) && variantOptions.includes(fabricRadio.value);
              });

              if (alternativeVariant && alternativeVariant.available) {
                // Clear all fabric selections
                fabricRadios.forEach((radio) => {
                  radio.removeAttribute("checked");
                  radio.checked = false;
                  // Remove visual classes
                  const listItem = radio.closest("li");
                  const label = listItem?.querySelector("label");
                  if (label) {
                    label.classList.remove("!bg-black", "!text-white", "!outline-black");
                    label.style.zIndex = "";
                  }
                });

                // Select the alternative fabric
                fabricRadio.setAttribute("checked", "checked");
                fabricRadio.checked = true;

                // Add visual classes
                const listItem = fabricRadio.closest("li");
                const label = listItem?.querySelector("label");
                if (label) {
                  label.classList.add("!bg-black", "!text-white", "!outline-black");
                  label.style.zIndex = "2";
                }
                return; // Exit early if fabric switch worked
              }
            }
          }
        }
      }
    },

    /**
     * Update visual selected state of color swatches
     */
    updateSelectedState(clickedElement, colorList) {
      if (colorList) {
        const allItems = colorList.querySelectorAll("li");
        allItems.forEach((li) => {
          const borderElements = li.querySelectorAll(".border-b");
          borderElements.forEach((el) => {
            el.classList.remove("border-black");
            el.classList.add("border-transparent");
          });
          const links = li.querySelectorAll("a, button");
          links.forEach((link) => {
            link.removeAttribute("aria-current");
          });
        });
      }

      const clickedLinks = clickedElement.querySelectorAll("a, button");
      clickedLinks.forEach((link) => {
        link.setAttribute("aria-current", "page");
      });

      const borderElements = clickedElement.querySelectorAll(".border-b");
      borderElements.forEach((el) => {
        el.classList.remove("border-transparent");
        el.classList.add("border-black");
      });
    },

    /**
     * Update the media gallery
     */
    updateMediaGallery(currentPdpSection, doc) {
      const currentMediaSection = currentPdpSection.querySelector("section[ax-load]");
      const newMediaSection = doc.querySelector("section[ax-load]");

      if (currentMediaSection && newMediaSection) {
        // More thorough Alpine cleanup - set Alpine properties to safe defaults
        if (window.Alpine) {
          // Set Alpine's internal data to empty array instead of deleting
          // This prevents errors when Alpine tries to filter the dataStack
          if (currentMediaSection._x_dataStack) {
            currentMediaSection._x_dataStack = [];
          }
          // Set other Alpine properties to safe defaults
          Object.keys(currentMediaSection).forEach((key) => {
            if (key.startsWith("_x_")) {
              // For arrays, set to empty array; for objects, set to empty object; otherwise set to null
              if (Array.isArray(currentMediaSection[key])) {
                currentMediaSection[key] = [];
              } else if (typeof currentMediaSection[key] === "object" && currentMediaSection[key] !== null) {
                currentMediaSection[key] = {};
              } else {
                currentMediaSection[key] = null;
              }
            }
          });
        }

        const parent = currentMediaSection.parentElement;
        const newGallery = newMediaSection.cloneNode(true);

        // Replace the element
        parent.replaceChild(newGallery, currentMediaSection);

        // Re-initialize Alpine on the new element if needed
        if (window.Alpine && newGallery.hasAttribute("ax-load")) {
          // Use nextTick to ensure DOM is ready
          setTimeout(() => {
            try {
              window.Alpine.initTree(newGallery);
            } catch (error) {
              console.warn("Alpine initTree error in media gallery:", error);
            }
          }, 0);
        }
      }
    },

    /**
     * Update price immediately from variantData + checked options (no network wait).
     */
    syncPriceFromSelection(sectionElement, sectionId) {
      if (!sectionId) return;

      const pickerWrapper = this.findVariantPickerElement(sectionElement);
      const variantDataScript = pickerWrapper?.querySelector('script[x-ref="variantData"]');
      if (!variantDataScript) return;

      let variantData;
      try {
        variantData = JSON.parse(variantDataScript.textContent);
      } catch (e) {
        console.error("Failed to parse variant data for price sync:", e);
        return;
      }

      const variantSelects =
        document.querySelector(`#variant-selects-${sectionId}`) ||
        document.querySelector(`#bundle-selects-${sectionId}`);
      if (!variantSelects) return;

      const selectedOptions = {};
      variantSelects.querySelectorAll('input[type="radio"]:checked').forEach((radio) => {
        if (radio.name !== "bundle_size_from_title") {
          selectedOptions[radio.name] = radio.value;
        }
      });

      const match = findMatchingVariantFromSelection(variantData, selectedOptions);
      if (match) {
        applyVariantPriceToDom(sectionId, match);
      }
    },

    /**
     * Update comparison pricing tables and bedding mobile drawer content when switching colors.
     * Uses the already-fetched section HTML so competitor metafields and RF price stay in sync.
     */
    updatePricingTables(sectionElement, doc) {
      if (!sectionElement || !doc) return;

      // Live tables (fashion mobile + desktop, including A/B copies with unique ids)
      sectionElement.querySelectorAll('[id^="product-pricing-table-"]').forEach((currentTable) => {
        const newTable = doc.getElementById(currentTable.id);
        if (newTable) {
          currentTable.innerHTML = newTable.innerHTML;
        }
      });

      // Bedding mobile: table HTML is stored on the drawer button's data-content
      sectionElement.querySelectorAll("#product-comparison-wrap button[data-content]").forEach((btn) => {
        const newBtn = doc.querySelector("#product-comparison-wrap button[data-content]");

        if (newBtn?.getAttribute("data-content")) {
          btn.setAttribute("data-content", newBtn.getAttribute("data-content"));
        }
      });
    },

    /**
     * Update product title when switching colors.
     * Price and buttons are left to renderProductInfo so the selected size variant is preserved.
     * Yotpo bottomline is synced in place (product id only) to avoid wiping rendered stars.
     */
    updateProductTitleOnly(sectionElement, doc, sectionId) {
      if (!sectionId) return;

      const titleSelector = `#product-title-${sectionId}`;
      const currentTitle = sectionElement.querySelector(`.product-reviews-title-price--main ${titleSelector}`);
      const newTitle = doc.querySelector(`.product-reviews-title-price--main ${titleSelector}`);

      if (currentTitle && newTitle) {
        currentTitle.innerHTML = newTitle.innerHTML;
        if (newTitle.dataset.colourAppended != null) {
          currentTitle.dataset.colourAppended = newTitle.dataset.colourAppended;
        }
      }

      this.syncYotpoProductIds(sectionElement, doc);
    },

    /**
     * Keep rendered Yotpo bottomline stars; only update product ids for the new color.
     * Replacing innerHTML with an empty .yotpo.bottomLine shell causes a visible flash.
     */
    syncYotpoProductIds(sectionElement, doc) {
      const newProductId = this.getYotpoProductIdFromDoc(doc);
      if (!newProductId) return;

      const root = sectionElement.querySelector(".product-reviews-title-price--main") || sectionElement;

      root.querySelectorAll(".yotpo.bottomLine[data-product-id]").forEach((el) => {
        el.setAttribute("data-product-id", newProductId);
      });

      root.querySelectorAll("[data-yotpo-product-id]").forEach((el) => {
        el.setAttribute("data-yotpo-product-id", newProductId);
      });

      root.querySelectorAll("button[data-product-id]").forEach((btn) => {
        if (btn.querySelector(".yotpo.bottomLine")) {
          btn.setAttribute("data-product-id", newProductId);
        }
      });

      // Reviews row under details (product-info-buttons)
      sectionElement.querySelectorAll('[id^="product-buttons"] button[data-product-id]').forEach((btn) => {
        if (btn.querySelector(".yotpo.bottomLine")) {
          btn.setAttribute("data-product-id", newProductId);
          btn.querySelectorAll(".yotpo.bottomLine[data-product-id]").forEach((el) => {
            el.setAttribute("data-product-id", newProductId);
          });
          btn.querySelectorAll("[data-yotpo-product-id]").forEach((el) => {
            el.setAttribute("data-yotpo-product-id", newProductId);
          });
        }
      });

      // Inline reviews widget further down the page (drawer clones from this)
      document.querySelectorAll("#yotpo-reviews-inline [data-yotpo-product-id]").forEach((el) => {
        el.setAttribute("data-yotpo-product-id", newProductId);
      });
    },

    getYotpoProductIdFromDoc(doc) {
      const fromBottomline = doc
        .querySelector(".product-reviews-title-price--main .yotpo.bottomLine[data-product-id]")
        ?.getAttribute("data-product-id");
      if (fromBottomline) return fromBottomline;

      const fromButton = doc
        .querySelector(".product-reviews-title-price--main button[data-product-id]")
        ?.getAttribute("data-product-id");
      if (fromButton) return fromButton;

      return (
        doc.querySelector("#yotpo-reviews-inline [data-yotpo-product-id]")?.getAttribute("data-yotpo-product-id") ||
        doc
          .querySelector('[id^="product-buttons"] .yotpo.bottomLine[data-product-id]')
          ?.getAttribute("data-product-id") ||
        null
      );
    },

    /**
     * Update product title and price section when switching colors
     */
    updateProductTitleAndPrice(sectionElement, doc, sectionId) {
      // Use the visible main block, not the hidden --top block (both have #product-purchase-info)
      const currentPurchaseInfo = sectionElement.querySelector(
        ".product-reviews-title-price--main #product-purchase-info"
      );
      const newPurchaseInfo = doc.querySelector(".product-reviews-title-price--main #product-purchase-info");
      const newPurchaseInfoAlt = doc.querySelector("#product-purchase-info");
      const newPurchaseInfoSource = newPurchaseInfo || newPurchaseInfoAlt;

      if (currentPurchaseInfo && newPurchaseInfoSource) {
        const clonedPurchaseInfo = newPurchaseInfoSource.cloneNode(true);
        currentPurchaseInfo.parentElement.replaceChild(clonedPurchaseInfo, currentPurchaseInfo);
        return;
      }

      // Fallback: update price element directly if product-purchase-info structure differs
      if (sectionId) {
        const currentPrice = sectionElement.querySelector(`#price-${sectionId}`);
        const newPrice = doc.querySelector(`#price-${sectionId}`);

        if (currentPrice && newPrice) {
          currentPrice.innerHTML = newPrice.innerHTML;
        }
      }
    },

    /**
     * Update product buttons section
     */
    updateProductButtons(sectionElement, doc) {
      // Find the product buttons container
      const currentButtons = sectionElement.querySelector('[id^="product-buttons"]');
      const newButtons = doc.querySelector('[id^="product-buttons"]');

      if (currentButtons && newButtons) {
        const parent = currentButtons.parentElement;
        const clonedButtons = newButtons.cloneNode(true);
        parent.replaceChild(clonedButtons, currentButtons);

        // Note: Alpine re-initialization not needed here because the buttons are inside
        // the variant picker, which already re-initializes Alpine when it's replaced
      }
    },

    /**
     * Update variant picker while preserving color swatches
     * @param {string|null} [preservedSize] - Size to keep across the colour swap
     */
    updateVariantPicker(sectionElement, doc, preservedSize) {
      const currentVariantPickerWrapper = this.findVariantPickerElement(sectionElement);
      const newVariantPickerWrapper = this.findVariantPickerElement(doc.body);

      if (currentVariantPickerWrapper && newVariantPickerWrapper) {
        // Modify the x-data attribute to add true argument to variantPicker function
        const xDataAttr = newVariantPickerWrapper.getAttribute("x-data");
        if (xDataAttr && xDataAttr.includes("variantPicker")) {
          // Match the variantPicker function call and add true as 4th argument
          // Pattern: variantPicker(false, false, '/products/...')
          const modifiedXData = xDataAttr.replace(/variantPicker\((.*?)\)/, (match, args) => {
            // Split existing arguments and add true at the end
            const argsList = args.split(",").map((arg) => arg.trim());
            argsList.push("true");
            if (preservedSize) {
              argsList.push(JSON.stringify(preservedSize));
            }
            return `variantPicker(${argsList.join(", ")})`;
          });
          newVariantPickerWrapper.setAttribute("x-data", modifiedXData);
        }

        // Save the color swatches before replacing
        const colorSwatchesList = currentVariantPickerWrapper.querySelector("ul.color-swatches-list");

        // Clone the new wrapper (not in DOM yet)
        const newWrapper = newVariantPickerWrapper.cloneNode(true);

        // Copy the data-is-preorder attribute to the cloned wrapper if it exists
        if (newVariantPickerWrapper.hasAttribute("data-is-preorder")) {
          newWrapper.setAttribute("data-is-preorder", "true");
        }

        // Copy the data-cta-atb attribute to the cloned wrapper if it exists
        if (newVariantPickerWrapper.hasAttribute("data-cta-atb")) {
          newWrapper.setAttribute("data-cta-atb", "true");
        }

        // Restore color swatches in the cloned wrapper BEFORE inserting into DOM
        if (colorSwatchesList) {
          const newColorSwatchesContainer = newWrapper.querySelector("section.color-swatches-container");
          if (newColorSwatchesContainer) {
            const newList = newColorSwatchesContainer.querySelector("ul.color-swatches-list");
            if (newList) {
              newList.replaceWith(colorSwatchesList.cloneNode(true));
            }
          }
        }

        // Destroy Alpine on the old element to prevent double initialization
        if (window.Alpine && currentVariantPickerWrapper._x_dataStack) {
          // Set Alpine's internal data to empty array instead of deleting
          // This prevents errors when Alpine tries to filter the dataStack
          currentVariantPickerWrapper._x_dataStack = [];
        }

        // Now replace in DOM - Alpine will auto-initialize with all our setup
        const parent = currentVariantPickerWrapper.parentElement;
        parent.replaceChild(newWrapper, currentVariantPickerWrapper);
      }
    },

    /**
     * Find the variant picker Alpine component element
     */
    findVariantPickerElement(rootElement) {
      const allWrappers = rootElement?.querySelectorAll("[x-data]");
      if (allWrappers) {
        for (const el of allWrappers) {
          const xDataAttr = el.getAttribute("x-data");
          if (xDataAttr && xDataAttr.includes("variantPicker")) {
            return el;
          }
        }
      }
      return null;
    },

    /**
     * Reset thumbnail scroll position
     */
    resetThumbnailScroll(pdpSection) {
      const allElements = pdpSection.querySelectorAll("[x-ref]");
      for (const el of allElements) {
        if (el.getAttribute("x-ref") === "thumbnailContainer") {
          el.scrollLeft = 0;
          break;
        }
      }
    },

    /**
     * Select size on the new colour product.
     * Keeps the current size whenever it exists on the new colour (including master).
     * Falls back to first available only when that size is missing.
     */
    selectSize(currentSize, sectionId) {
      const variantSelects = sectionId
        ? document.querySelector(`#variant-selects-${sectionId}`) ||
          document.querySelector(`#bundle-selects-${sectionId}`)
        : document.querySelector('[id^="variant-selects-"]') || document.querySelector('[id^="bundle-selects-"]');

      if (!variantSelects) {
        return;
      }

      const sizeInputs = radiosByOptionName(variantSelects, "Size");
      if (!sizeInputs.length) {
        return;
      }

      const sizeValues = new Set(sizeInputs.map((input) => input.value));
      const sizeToKeep = currentSize || window._pendingColorSwitchSize;
      let sizeToSelect = sizeToKeep && sizeValues.has(sizeToKeep) ? sizeToKeep : null;

      if (!sizeToSelect) {
        const pickerWrapper = variantSelects.closest('[x-data*="variantPicker"]');
        const variantDataScript = pickerWrapper?.querySelector('script[x-ref="variantData"]');

        if (variantDataScript) {
          let variantData = [];
          try {
            variantData = JSON.parse(variantDataScript.textContent);
          } catch (e) {
            console.error("Failed to parse variant data for size select:", e);
          }

          const firstAvailable = variantData.find((variant) => variant.available);
          if (firstAvailable) {
            sizeToSelect = [firstAvailable.option1, firstAvailable.option2, firstAvailable.option3].find((opt) =>
              sizeValues.has(opt)
            );
          }
        }
      }

      if (!sizeToSelect) {
        return;
      }

      const sizeInput = sizeInputs.find((input) => input.value === sizeToSelect);
      if (!sizeInput) {
        return;
      }

      sizeInput.closest(".variant-option")?.querySelector("label")?.click();

      const pickerWrapper = variantSelects.closest('[x-data*="variantPicker"]');
      const pickerData = pickerWrapper && window.Alpine ? window.Alpine.$data(pickerWrapper) : null;

      if (pickerData?.options?.length && typeof pickerData.getActualOptionNames === "function") {
        const optionNames = pickerData.getActualOptionNames() || [];
        const sizeIndex = optionNames.findIndex((name) => name.toLowerCase() === "size");

        if (sizeIndex >= 0 && pickerData.options[sizeIndex]) {
          pickerData.options[sizeIndex].value = sizeToSelect;
          pickerData.options = [...pickerData.options];
          if (typeof pickerData.syncRadioButtons === "function") {
            pickerData.syncRadioButtons();
          }
        }
      }
    }
  };
}

export default colorSwitcher;
